/**
 * PortalTaxReview — the deep-link target of every tax-cadence email.
 *
 * Client sees the same figures from the email, plus two obvious choices:
 *   ✓ Figures look correct       → job moves to accountant review
 *   ✎ I need to make a change    → free-text + optional receipt upload
 *
 * Slice C MVP: thin page, no charts / transaction drill-down (that's Slice D).
 * The URL is /portal/tax/review/:jobId, embedded into every cadence email.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, PencilLine, Paperclip, X, ArrowLeft } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const KIND_LABELS = {
    gst_return: "GST return",
    annual_return: "Annual tax return",
    provisional_tax: "Provisional tax payment",
};

const CATEGORIES = [
    ["missing_income", "Missing income"],
    ["missing_expense", "Missing expense"],
    ["incorrect_txn", "Incorrect transaction"],
    ["other", "Something else"],
];

export default function PortalTaxReview() {
    const { jobId } = useParams();
    const nav = useNavigate();
    const [state, setState] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [mode, setMode] = useState(null); // null | "confirmed" | "change"
    const [explanation, setExplanation] = useState("");
    const [category, setCategory] = useState("other");
    const [attachments, setAttachments] = useState([]);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/tax/review/${jobId}`);
            setState(data);
            if (data.job.client_review_status === "confirmed") setMode("confirmed");
            if (data.job.client_review_status === "change_requested") setMode("done_change");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [jobId]);
    useEffect(() => { load(); }, [load]);

    async function confirm() {
        setBusy(true);
        try {
            await api.post(`/tax/review/${jobId}/confirm`, {});
            toast.success("Thanks — we've marked the figures as confirmed.");
            setMode("confirmed");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    async function handleFiles(fileList) {
        const files = Array.from(fileList || []);
        const encoded = [];
        for (const f of files) {
            if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10 MB`); continue; }
            const buf = await f.arrayBuffer();
            const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
            encoded.push({ name: f.name, content_type: f.type || "application/octet-stream", content_base64: b64, size: f.size });
        }
        setAttachments((a) => [...a, ...encoded]);
    }

    async function submitChange() {
        if (explanation.trim().length < 3) { toast.error("Please tell us what needs to change."); return; }
        setBusy(true);
        try {
            await api.post(`/tax/review/${jobId}/change-request`, {
                explanation, category,
                attachments: attachments.map((a) => ({ name: a.name, content_type: a.content_type, content_base64: a.content_base64 })),
            });
            toast.success("Thanks — we've received your changes and will get back to you.");
            setMode("done_change");
            setExplanation(""); setAttachments([]);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    if (loading) return <div className="p-12 text-center text-sm text-[#5E636E]"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading your review…</div>;
    if (!state) return <div className="p-12 text-center text-sm text-[#5E636E]">We couldn&apos;t load this review. Please contact your accountant.</div>;

    const { job, context } = state;
    const kindLabel = KIND_LABELS[job.kind] || "Tax return";

    return (
        <div className="max-w-2xl mx-auto space-y-4">
            <button onClick={() => nav("/portal")} className="text-sm text-[#5E636E] hover:text-[#0F1115] inline-flex items-center gap-1" data-testid="tax-review-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to portal
            </button>

            <Card className="p-6">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">{kindLabel}</div>
                <h1 className="mt-1 text-2xl font-semibold text-[#0F1115]">{job.period_label || kindLabel}</h1>
                <div className="mt-3 text-sm text-[#5E636E]">
                    <b>IRD deadline:</b> {context.ird_deadline} · <b>Our target filing date:</b> {context.cops_target_date}
                </div>

                {/* Money summary — same numbers as email */}
                {job.kind === "gst_return" && (
                    <div className="mt-5 rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4 space-y-1.5 text-[13.5px]" data-testid="tax-review-summary">
                        <SummaryRow label="GST collected" value={context.gst_collected} />
                        <SummaryRow label="GST claimed" value={context.gst_claimed} />
                        <div className="border-t border-[#E4E4E7] pt-2 mt-2 flex justify-between font-semibold text-[15px]">
                            <span>GST payable</span><span className="tabular-nums">{context.amount_display}</span>
                        </div>
                    </div>
                )}
                {job.kind === "annual_return" && (
                    <div className="mt-5 rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4 space-y-1.5 text-[13.5px]" data-testid="tax-review-summary">
                        <SummaryRow label="Revenue" value={context.revenue_display} />
                        <SummaryRow label="Business expenses" value={context.expenses_display} />
                        <SummaryRow label="Net profit" value={context.net_profit_display} />
                        <div className="border-t border-[#E4E4E7] pt-2 mt-2 flex justify-between font-semibold text-[15px]">
                            <span>Tax position</span><span className="tabular-nums">{context.amount_display}</span>
                        </div>
                    </div>
                )}
                {job.kind === "provisional_tax" && (
                    <div className="mt-5 rounded-lg border border-[#E4E4E7] bg-[#FAFAF7] p-4 space-y-1.5 text-[13.5px]" data-testid="tax-review-summary">
                        <div className="flex justify-between"><span className="text-[#5E636E]">Payment date</span><span className="tabular-nums">{context.ird_deadline}</span></div>
                        <div className="border-t border-[#E4E4E7] pt-2 mt-2 flex justify-between font-semibold text-[15px]">
                            <span>Amount</span><span className="tabular-nums">{context.amount_display}</span>
                        </div>
                    </div>
                )}

                {mode === "confirmed" && (
                    <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-[13px] text-emerald-900" data-testid="tax-review-confirmed">
                        <div className="flex items-center gap-2 font-semibold"><Check className="h-4 w-4" /> Thanks — figures confirmed.</div>
                        <div className="mt-1">Your accountant will do a final review before filing. You&apos;ll get a confirmation email once it&apos;s lodged with IRD.</div>
                    </div>
                )}
                {mode === "done_change" && (
                    <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-[13px] text-[#78511A]" data-testid="tax-review-changed">
                        <div className="font-semibold">Thanks — we&apos;ve got your change request.</div>
                        <div className="mt-1">Your accountant will review the info and get back to you before we file.</div>
                    </div>
                )}

                {mode == null && (
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tax-review-actions">
                        <Button
                            onClick={confirm} disabled={busy}
                            className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-[14px]"
                            data-testid="tax-review-confirm"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Figures look correct</>}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setMode("change")}
                            className="h-11 text-[14px]"
                            data-testid="tax-review-change-open"
                        >
                            <PencilLine className="h-4 w-4 mr-1" /> I need to make a change
                        </Button>
                    </div>
                )}

                {mode === "change" && (
                    <div className="mt-6 space-y-3" data-testid="tax-review-change-form">
                        <div>
                            <Label className="text-[12px] text-[#5E636E]">What kind of change?</Label>
                            <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {CATEGORIES.map(([v, l]) => (
                                    <button key={v} type="button" onClick={() => setCategory(v)}
                                        className={"rounded-full border px-3 h-8 text-[12px] " + (category === v ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:bg-zinc-50")}
                                        data-testid={`tax-review-cat-${v}`}>
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-[12px] text-[#5E636E]">Tell us in your own words — no accounting jargon needed</Label>
                            <Textarea rows={5} value={explanation} onChange={(e) => setExplanation(e.target.value)}
                                placeholder="e.g. I paid $850 cash for tools in July that isn't in Xero — receipt attached."
                                className="mt-1" data-testid="tax-review-explanation" />
                        </div>
                        <div>
                            <Label className="text-[12px] text-[#5E636E]">Add receipts / documents (optional)</Label>
                            <label className="mt-1 flex items-center gap-2 rounded-md border border-dashed border-[#E4E4E7] bg-[#FAFAF7] px-3 py-3 cursor-pointer hover:bg-white" data-testid="tax-review-upload-wrap">
                                <Paperclip className="h-4 w-4 text-[#5E636E]" />
                                <span className="text-sm text-[#5E636E]">Click to add files (10 MB max each)</span>
                                <input type="file" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="tax-review-upload-input" />
                            </label>
                            {attachments.length > 0 && (
                                <ul className="mt-2 space-y-1 text-[12px]" data-testid="tax-review-upload-list">
                                    {attachments.map((a, i) => (
                                        <li key={i} className="flex items-center gap-2 rounded border border-[#E4E4E7] bg-white px-2 py-1">
                                            <Paperclip className="h-3 w-3 text-[#5E636E]" />
                                            <span className="truncate flex-1">{a.name}</span>
                                            <span className="text-[10px] text-[#5E636E]">{(a.size / 1024).toFixed(1)} KB</span>
                                            <button type="button" onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-[#5E636E] hover:text-red-600">
                                                <X className="h-3 w-3" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="flex gap-2 pt-2">
                            <Button variant="outline" onClick={() => setMode(null)} disabled={busy}>Cancel</Button>
                            <Button onClick={submitChange} disabled={busy || explanation.trim().length < 3} className="bg-[#0F1115] hover:bg-[#2E2E2E] text-white" data-testid="tax-review-change-submit">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send to my accountant"}
                            </Button>
                        </div>
                    </div>
                )}
            </Card>
        </div>
    );
}

function SummaryRow({ label, value }) {
    return (
        <div className="flex justify-between">
            <span className="text-[#5E636E]">{label}</span>
            <span className="tabular-nums">{value}</span>
        </div>
    );
}
